import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  /** v2.2 P2: one button on the toast — "Undo" after a delete. */
  action?: ToastAction;
  durationMs?: number;
}

interface Toast {
  id: number;
  message: string;
  tone: "ok" | "danger";
  action?: ToastAction;
}

const ToastContext = createContext<{
  push: (message: string, tone?: Toast["tone"], options?: ToastOptions) => void;
}>({
  push: () => undefined,
});

/** §7.10: a toast on every mutation. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast["tone"] = "ok", options?: ToastOptions) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone, action: options?.action }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), options?.durationMs ?? 5000);
  }, []);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            data-testid="toast"
            className={
              "pointer-events-auto max-w-lg rounded border px-3 py-2 text-sm shadow-lg " +
              (t.tone === "danger"
                ? "border-danger/40 bg-danger/15 text-danger"
                : "border-ok/40 bg-ok/15 text-ok")
            }
          >
            {t.message}
            {t.action && (
              <button
                type="button"
                className="ml-3 font-semibold underline underline-offset-2"
                data-testid="toast-action"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// The provider and its hook belong in one file; splitting them across three files to satisfy
// react-refresh would cost more clarity than the fast-refresh edge case is worth.
// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  return useContext(ToastContext);
}
